// ---------------------------------------------------------------------------
// AutoMate — feature flags and static resources
// ---------------------------------------------------------------------------

/**
 * Master switch for the AutoMate AI Search feature.
 *
 * When false, the AISearchBar, SearchPopup, and AutoMateBubble are all hidden.
 * Flip to true once the agent backend is wired up for the current environment.
 */
export const AI_SEARCH_ENABLED = false;

//Adding this until complete PROD merge from BETA happen - Please remove this once merging from BETA to PROD
export const AI_AGENT_ENABLED = false;

/**
 * When true, the ChatPanel displays the time-to-first-token and
 * time-to-last-token for each assistant response, and both values are
 * logged to the console. Useful while iterating on the agent's latency.
 *
 * Flip to false to hide the timing chips and silence the console logs.
 */
export const AI_AGENT_SHOW_TIMING = false;

/**
 * When true, emits verbose debug logs across the AutoMate AI pipeline:
 *   - `AISearchAppSync.js` — WebSocket lifecycle, AppSync protocol frames,
 *     raw chunk payloads
 *   - `useAgentChat`       — submit, mutation ack, each STREAMING/COMPLETE/
 *     ERROR chunk, cancel
 *   - `useAgentSuggestions` — onQueryChange, cache hits, debounce arming,
 *     searchAgent fire/ack, terminal chunk titles
 *   - `SearchPopup`         — new-search dispatch, searchAgent ack, each
 *     STREAMING/COMPLETE/ERROR chunk
 *
 * Logs include user questions and AI-generated responses. Leave false in
 * production/beta. Errors and unexpected transport states are still logged
 * unconditionally via `console.error` / `console.warn` regardless of this flag.
 */
export const AI_AGENT_DEBUG_LOGS = false;

/**
 * When true, the Search path (SearchPopup) calls the Quick Search endpoint
 * before invoking searchAgent, rendering fast structured results while the
 * full AI answer streams in behind them.
 *
 * Flip to true once the quick-search Lambda is verified for the current stage.
 */
export const SEARCH_AGENT_QUICK_SEARCH_ENABLED = true;

/**
 * When true, the Chat path (ChatPanel) calls the Quick Search endpoint
 * before invoking sendAgentMessage, rendering a fast structured result card
 * while the full AI answer streams in behind it. This flag also covers the
 * AutoMateBubble, since both surfaces share the same AgentChatProvider chat
 * path.
 *
 * Flip to true once the quick-search Lambda is verified for the current stage.
 */
export const CHAT_AGENT_QUICK_SEARCH_ENABLED = false;

/**
 * Number of structured search results (KB references) to request from the
 * search agent. Threaded through `searchAgent` → the orchestrator's KB
 * `numberOfResults` retrieval config. When unset the backend falls back to
 * Bedrock's implicit default (5), so set it explicitly to control how many
 * results the SearchPopup renders.
 */
export const SEARCH_AGENT_MAX_RESULTS = 10;

// ---------------------------------------------------------------------------
// Timeout thresholds for the agent response
//
// The agent occasionally takes a while to produce its first token (complex
// queries, orchestrator cold starts) or can stall mid-stream. These thresholds
// drive the soft / hard timeouts in `useAgentChat`:
//
//   - At `FIRST_TOKEN_WARN_MS` with no chunks yet → show a "still waiting"
//     banner but keep the subscription open; if a chunk arrives, the banner
//     is cleared and streaming continues normally.
//   - At `INTER_CHUNK_WARN_MS` between consecutive chunks → same soft banner.
//   - At `HARD_TIMEOUT_MS` total elapsed → give up: unsubscribe, replace the
//     assistant placeholder with an error message, and re-enable the input.
//
// Pick values comfortably above observed p99 latency so we don't cry wolf.
// ---------------------------------------------------------------------------

/** Show a "still waiting" banner if no first token arrives after this many ms. */
export const AI_AGENT_FIRST_TOKEN_WARN_MS = 30_000;

/** Show a "still waiting" banner if no new chunk arrives for this many ms mid-stream. */
export const AI_AGENT_INTER_CHUNK_WARN_MS = 20_000;

/** Hard ceiling — after this many ms total we stop waiting and show an error. */
export const AI_AGENT_HARD_TIMEOUT_MS = 120_000;

// ---------------------------------------------------------------------------
// Apps Library repository-search feature flag
// ---------------------------------------------------------------------------

/**
 * Independent switch for the Apps Library agent-backed repository search.
 *
 * When true, AppOpenSearchBar fires autocomplete requests and Apps.jsx
 * dispatches repository-search queries through `useRepositorySearch`.
 *
 * When false:
 *   - The agent search bar and autocomplete lifecycle are not rendered.
 *   - The legacy Polaris Autosuggest searches the cached app collection by
 *     app name and description.
 *   - Existing Apps Library filters remain visible and compose with the
 *     cached text search.
 *   - No repository-search or agent-autocomplete calls are made.
 *   - Any active in-flight repository search is reset and unsubscribed safely.
 *
 * This switch is independent of `AI_AGENT_ENABLED` (the Close Portal AI
 * search switch). Disabling one does not affect the other.
 *
 * Flip to true once the agent repository backend and KB re-export are
 * verified for the current stage.
 *
 * Requirements: 10.4, 10.5
 */
export const APPS_LIBRARY_REPO_SEARCH_ENABLED = true;

// ---------------------------------------------------------------------------
// Feedback UI feature flags
// ---------------------------------------------------------------------------

/**
 * Master switch for the chat-side feedback UI (thumbs-up / thumbs-down).
 *
 * When true, FeedbackButtons render adjacent to every assistant message in
 * ChatPanel. Default false; flip per-stage when the backend feedback path is
 * verified.
 */
export const CHAT_AGENT_FEEDBACK_ENABLED = true;

/**
 * Master switch for the search-side feedback UI (thumbs-up / thumbs-down).
 *
 * When true, FeedbackButtons render on the SearchPopup result area.
 * Default false; flip per-stage when the backend feedback path is verified.
 */
export const SEARCH_AGENT_FEEDBACK_ENABLED = true;
